import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { authServices } from "~/services/auth";

type FieldType = {
  email: string;
};

export const RequestPasswordResetView = () => {
  const api = useNotification();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: authServices.requestPasswordReset,
    onSuccess: () => {
      api.success({
        message: "Reset Password",
        description: "A password reset link has been sent to your email.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      api.error({
        message: "Request Failed",
        description: error.response?.data?.message || "Something went wrong",
      });
    },
  });

  const onFinish = (values: FieldType) => {
    mutate(values.email);
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4">
      <div className="text-center text-white mb-8 max-w-2xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Reset Password</h1>
        <p className="text-lg text-white/80">
          Enter your email to receive a password reset link.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Reset Your Password
          </h2>
          <p className="text-gray-600 mt-1">
            We'll send you a link to reset your password.
          </p>
        </div>

        <Form
          layout="vertical"
          onFinish={onFinish}
          className="space-y-6"
          requiredMark={CustomizeRequiredMark}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input
              size="large"
              id="email"
              type="email"
              placeholder="Enter your email"
            />
          </Form.Item>

          <Button
            loading={isPending}
            htmlType="submit"
            size="large"
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white py-2"
          >
            Send Reset Link
          </Button>

          <div className="text-center text-sm text-gray-600">
            Back to{" "}
            <Link
              to="/login"
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Sign in
            </Link>
          </div>
        </Form>
      </div>
    </main>
  );
};
